import React, { useState, useEffect, useRef } from "react";
import { useAuthStore } from "../../../store/authStore";
import { useManagementStore } from "../../../store/managementStore";
import {
	MagnifyingGlassIcon,
	CalendarDaysIcon,
	XMarkIcon,
	PlusIcon,
} from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import ScheduleDetailModal from "./ScheduleDetailModal";
import RegisterScheduleModal from "./RegisterScheduleModal";
import EditScheduleModal from "./EditScheduleModal";
import { BiTrip } from "react-icons/bi";

export default function ManagementSchedule() {
	const { user } = useAuthStore();
	const { getScheduleInfo, isLoading, error } = useManagementStore();
	const isMountedRef = useRef(false);

	// 상태 관리
	const [selectedYear, setSelectedYear] = useState(
		new Date().getFullYear().toString()
	);
	const [keyword, setKeyword] = useState("");
	const [scheduleData, setScheduleData] = useState([]);
	const [localLoading, setLocalLoading] = useState(false);

	// 모달 관련 상태
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
	const [isEditModalOpen, setIsEditModalOpen] = useState(false);
	const [selectedSchedule, setSelectedSchedule] = useState(null);
	const [scheduleToEdit, setScheduleToEdit] = useState(null);
	const [selectedEventData, setSelectedEventData] = useState(null);

	// 현재 연도부터 과거 10년까지의 연도 목록 생성
	const currentYear = new Date().getFullYear();
	const years = Array.from({ length: 10 }, (_, i) =>
		(currentYear - i).toString()
	);

	// 페이지 로드 시 초기 데이터 로드
	useEffect(() => {
		isMountedRef.current = true;
		const controller = new AbortController();
		handleSearch({ signal: controller.signal });
		return () => {
			isMountedRef.current = false;
			controller.abort();
		};
	}, []);

	// 검색 핸들러
	const handleSearch = async (options = {}) => {
		const { signal } = options;
		try {
			if (signal?.aborted || !isMountedRef.current) return;
			setLocalLoading(true);
			const result = await getScheduleInfo(selectedYear, keyword, {
				signal,
			});
			if (signal?.aborted || !isMountedRef.current) return;
			if (result.success) {
				const newData = result.data;
				setScheduleData(newData);

				// 새로운 데이터로 모달 내용 갱신 (모달이 열려있는 경우)
				if (isModalOpen && selectedSchedule) {
					// 일정 데이터에서 현재 선택된 일정과 동일한 이벤트 찾기
					const updatedEvent = newData.find(
						(event) =>
							event.GOOD_TYPE_CD ===
								selectedSchedule.GOOD_TYPE_CD &&
							event.AREA_CD === selectedSchedule.AREA_CD &&
							event.GOOD_YY === selectedSchedule.GOOD_YY &&
							event.GOOD_SEQ === selectedSchedule.GOOD_SEQ
					);

					if (updatedEvent) {
						setSelectedSchedule(updatedEvent);
					}
				}
			} else {
				if (signal?.aborted || !isMountedRef.current) return;
				toast.error(
					result.error || "일정표 정보를 불러오는데 실패했습니다."
				);
			}
		} catch (err) {
			const isAbortError =
				err?.name === "AbortError" || err?.code === "ERR_CANCELED";
			if (isAbortError || signal?.aborted || !isMountedRef.current) return;
			console.error("일정표 검색 중 오류 발생:", err);
			toast.error("일정표 정보를 불러오는데 실패했습니다.");
		} finally {
			if (!signal?.aborted && isMountedRef.current) {
				setLocalLoading(false);
			}
		}
	};

	// 일정 상세 모달 표시 핸들러
	const showScheduleModal = (index) => {
		setSelectedSchedule(scheduleData[index]);
		setIsModalOpen(true);
	};

	// 모달 닫기 핸들러
	const closeModal = () => {
		setIsModalOpen(false);
		setSelectedSchedule(null);
	};

	const openRegisterModal = (eventData) => {
		setSelectedEventData(eventData);
		setIsRegisterModalOpen(true);
	};

	const closeRegisterModal = () => {
		setIsRegisterModalOpen(false);
		setSelectedEventData(null);
	};

	const openEditModal = (schedule, eventData) => {
		setScheduleToEdit(schedule);
		setSelectedEventData(eventData);
		setIsEditModalOpen(true);
	};

	const closeEditModal = () => {
		setIsEditModalOpen(false);
		setScheduleToEdit(null);
	};

	return (
		<div className="py-6">
			<div className="flex justify-between items-center mb-6">
				<div>
					<h1 className="text-2xl font-bold text-gray-900">
						일정 등록
					</h1>
					<p className="text-gray-600 mt-1">
						일정을 등록하고 확인하는 페이지입니다.
					</p>
				</div>
				{/* <button
          onClick={() => openRegisterModal()}
          className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md text-sm inline-flex items-center"
        >
          <PlusIcon className="h-4 w-4 mr-1" />
          신규 일정 등록
        </button> */}
			</div>

			{/* 검색 필터 영역 */}
			<div className="bg-white shadow rounded-lg p-6 mb-6">
				<div className="flex flex-wrap items-center gap-4">
					{/* 연도 선택 */}
					<div className="relative">
						<div className="flex items-center">
							<label
								htmlFor="selectedYear"
								className="mr-2 text-sm font-medium text-gray-700"
							>
								검색년도
							</label>
							<div className="relative">
								<select
									id="selectedYear"
									value={selectedYear}
									onChange={(e) =>
										setSelectedYear(e.target.value)
									}
									className="py-2 pl-4 pr-8 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 appearance-none"
								>
									{years.map((year) => (
										<option key={year} value={year}>
											{year}
										</option>
									))}
								</select>
								<div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
									<svg
										className="fill-current h-4 w-4"
										xmlns="http://www.w3.org/2000/svg"
										viewBox="0 0 20 20"
									>
										<path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
									</svg>
								</div>
							</div>
						</div>
					</div>

					{/* 키워드 검색 */}
					<div className="relative flex items-center">
						<div className="relative w-56">
							<input
								type="text"
								id="keyword"
								placeholder="키워드 입력"
								value={keyword}
								onChange={(e) => setKeyword(e.target.value)}
								className="pl-10 pr-8 py-2 w-full border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
							/>
							<MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
							{keyword && (
								<button
									onClick={() => setKeyword("")}
									className="absolute right-2 top-1/2 transform -translate-y-1/2"
								>
									<XMarkIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
								</button>
							)}
						</div>
					</div>

					{/* 검색 버튼 */}
					<button
						onClick={handleSearch}
						className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md text-sm shadow-sm hover:shadow-md transition duration-150 ease-in-out"
					>
						<div className="flex items-center">
							<MagnifyingGlassIcon className="h-4 w-4 mr-1" />
							검색하기
						</div>
					</button>
				</div>
			</div>

			{/* 일정표 테이블 */}
			<div className="bg-white shadow rounded-lg overflow-hidden">
				<div className="overflow-x-auto">
					<table className="min-w-full divide-y divide-gray-200">
						<thead className="bg-gray-50">
							<tr>
								<th
									scope="col"
									className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
								>
									번호
								</th>
								<th
									scope="col"
									className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
								>
									goodsCode
								</th>
								<th
									scope="col"
									className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
								>
									상품명
								</th>
								<th
									scope="col"
									className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
								>
									일정 타입 갯수
								</th>
								<th
									scope="col"
									className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
								>
									일정 보기
								</th>
							</tr>
						</thead>
						<tbody
							className="bg-white divide-y divide-gray-200"
							id="table-body"
						>
							{scheduleData.length > 0 ? (
								scheduleData.map((data, index) => {
									const goodsCode = `${data.GOOD_TYPE_CD}|${data.AREA_CD}|${data.GOOD_YY}|${data.GOOD_SEQ}`;
									return (
										<tr
											key={index}
											className="hover:bg-gray-50"
										>
											<td className="px-4 py-3 text-sm text-gray-900">
												{index + 1}
											</td>
											<td className="px-4 py-3 text-sm text-gray-900">
												{goodsCode}
											</td>
											<td className="px-4 py-3 text-sm text-gray-900">
												{data.GOOD_NM}
											</td>
											<td className="px-4 py-3 text-sm text-gray-900">
												{(data.schedules || []).length}
											</td>
											<td className="px-4 py-3 text-sm text-gray-900">
												<button
													onClick={() =>
														showScheduleModal(index)
													}
													className="bg-blue-600 hover:bg-blue-700 text-white py-1 px-3 rounded-md text-sm inline-flex items-center"
												>
													<CalendarDaysIcon className="h-4 w-4 mr-1" />
													일정확인
												</button>
											</td>
										</tr>
									);
								})
							) : (
								<tr>
									<td
										colSpan="5"
										className="px-4 py-4 text-center text-sm text-gray-500"
									>
										{isLoading || localLoading ? (
											"데이터를 불러오는 중..."
										) : (
											<div className="flex flex-col items-center">
												<BiTrip className="h-10 w-10 text-gray-400 mb-2" />
												<p>
													조회된 행사 정보가 없습니다.
												</p>
												<p className="text-sm">
													날짜를 선택하고 검색해
													주세요.
												</p>
											</div>
										)}
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>
			</div>

			{/* 로딩 오버레이 */}
			{(isLoading || localLoading) && (
				<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
					<div className="bg-white p-6 rounded-lg shadow-lg">
						<p className="text-lg">로딩중...</p>
					</div>
				</div>
			)}

			{/* 오류 메시지 */}
			{error && (
				<div className="fixed bottom-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded z-50">
					<span className="block sm:inline">{error}</span>
				</div>
			)}

			{/* 일정 상세 모달 */}
			<ScheduleDetailModal
				isOpen={isModalOpen}
				onClose={closeModal}
				scheduleData={selectedSchedule}
				onRegister={openRegisterModal}
				onRefresh={handleSearch} // 데이터 새로고침을 위한 함수 추가
				onEdit={openEditModal} // 수정 모달 열기 함수 추가
			/>

			{/* 일정 등록 모달 */}
			<RegisterScheduleModal
				isOpen={isRegisterModalOpen}
				onClose={closeRegisterModal}
				eventData={selectedEventData}
				onSuccess={handleSearch} // 일정 등록 성공 후 데이터 새로 불러오기
			/>

			{/* 일정 수정 모달 */}
			<EditScheduleModal
				isOpen={isEditModalOpen}
				onClose={closeEditModal}
				scheduleData={scheduleToEdit}
				eventData={selectedEventData}
				onSuccess={handleSearch} // 일정 수정 성공 후 데이터 새로 불러오기
				// onEdit={openEditModal}
			/>
		</div>
	);
}
